import { useDroppable } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext, type SortingStrategy } from '@dnd-kit/sortable';
import type { Header, Table as TanStackTable } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { type CSSProperties, Fragment, type ReactNode, useMemo } from 'react';

import type { TableStateControl } from '../../hooks';
import { getColumnFilter, removeColumnFilter, setColumnFilter } from '../../hooks/useTableStateControl.utils';
import { cn } from '../../styles/lib/utils';
import type { FilterLineItem, FilterOperatorType, FilterValueConfig } from '../../utils/filterSettings';
import {
  type ColumnFilter,
  ColumnFilterProvider,
  type ColumnMoveDirection,
  type ColumnResizeAction,
  type PinPosition,
  type SortDirection,
} from '../ColumnMenu';
import { Draggable } from '../Drag';
import { GridHeader } from '../Grid';
import { GridHead } from '../GridHead';
import { GridHeadCell } from '../GridHeadCell';
import { GridRow } from '../GridRow';
import { TableHeadGroup } from './TableHeadGroup';
import { getPinnedCellProps, type PinnedCellLayout, sumSizeVars, toCssVarName } from './utils';

/**
 * Dropzone component for the end of a section
 */
function EndDropzone({
  section,
  activeId,
  overId,
  stickyStyle,
}: {
  section: 'left' | 'center' | 'right';
  activeId: string | null;
  overId: string | null;
  /** Keeps a pinned section's dropzone beside its columns while the table scrolls. */
  stickyStyle?: CSSProperties;
}) {
  const dropzoneId = `${section}-end-dropzone`;
  const { setNodeRef, isOver } = useDroppable({
    id: dropzoneId,
  });

  const isActive = activeId !== null && (overId === dropzoneId || isOver);

  return (
    <td
      ref={setNodeRef}
      aria-hidden="true"
      data-pinned={section === 'center' ? undefined : section}
      className={cn(
        'self-stretch w-1 rounded-full transition-colors border-b border-grey-30 ml-[-2px] z-12 ',
        // Overlaps the last pinned column, so the drop indicator shows inside the pinned area.
        section === 'left' && 'ml-[-4px]',
        isActive ? 'bg-blue-40' : 'bg-transparent',
      )}
      style={stickyStyle}
    />
  );
}

type TableHeaderProps<TData> = {
  table: TanStackTable<TData>;
  columnGroups: { left: string[]; center: string[]; right: string[] };
  activeId: string | null;
  overId: string | null;
  /** Sticky layout of each visible pinned column. */
  pinnedLayouts: Map<string, PinnedCellLayout>;
  stateControl?: TableStateControl;
  isLoading?: boolean;
  fixedPinning?: boolean;
  /** Column id whose header keeps its width as flex basis and grows into leftover section space. */
  expandColumnId?: string;
  /** Renders the leaf header row for the data grid keyboard model (see useGridNavigation). */
  isGrid?: boolean;
  /** Sets aria-rowindex on header rows, for a partial page whose body rows carry it. */
  indexRows?: boolean;
  onMoveColumn?: (columnId: string, direction: ColumnMoveDirection) => void;
  onResizeColumn?: (columnId: string, action: ColumnResizeAction) => void;
};

type Section = 'left' | 'center' | 'right';

const SECTIONS: Section[] = ['left', 'center', 'right'];

/**
 * Table header component.
 * Renders each header row's left-pinned, center and right-pinned headers in visual order, with drag-and-drop support.
 */
function TableHeader<TData>({
  table,
  columnGroups,
  activeId,
  overId,
  pinnedLayouts,
  stateControl,
  isLoading = false,
  fixedPinning = false,
  expandColumnId,
  isGrid = false,
  indexRows = false,
  onMoveColumn,
  onResizeColumn,
}: TableHeaderProps<TData>) {
  const gridCellProps = (columnId: string) =>
    isGrid ? { 'data-grid-cell': '', 'data-column-id': columnId } : undefined;
  // Columns the Move buttons step through within a section: visible, and never the select column.
  const movableColumnIdsIn = (section: Section) =>
    columnGroups[section].filter((id) => id !== 'select' && table.getColumn(id)?.getIsVisible());

  /**
   * Width and column count of one group header INSTANCE in this section, summing only the leaves
   * under that instance: a group split by column order renders once per
   * contiguous run, and each run must size to its own columns, not the whole
   * group. Deduped by column id because at grouping depth ≥ 3 the header tree
   * pads shallower columns with placeholder headers that repeat a column.
   */
  const getHeaderSizeForSection = (header: Header<TData, unknown>, section: Section) => {
    const seenColumnIds = new Set<string>();
    let size = 0;
    let columnCount = 0;
    for (const leafHeader of header.getLeafHeaders()) {
      // getLeafHeaders returns the whole subtree, group headers included; only
      // true leaves count (a group can even share its id with a leaf, e.g. 'select').
      if (leafHeader.subHeaders.length > 0) continue;
      const { column } = leafHeader;
      if (seenColumnIds.has(column.id)) continue;
      seenColumnIds.add(column.id);
      if (column.getIsVisible() && columnGroups[section].includes(column.id)) {
        size += column.getSize();
        columnCount += 1;
      }
    }
    return { size, columnCount };
  };

  // A group band in a pinned section sticks with its first (left) or last (right) column in that section. Only an
  // empty band draws the pinned edge, stretched to its row so the line runs unbroken up the header; a labelled pill keeps its shape.
  const getBandLayout = (
    header: Header<TData, unknown>,
    section: Section,
    isEmpty: boolean,
  ): PinnedCellLayout | undefined => {
    if (section === 'center') return undefined;
    const leafIds = header
      .getLeafHeaders()
      .filter((h) => h.subHeaders.length === 0 && pinnedLayouts.get(h.column.id)?.side === section)
      .map((h) => h.column.id);
    const anchor = pinnedLayouts.get(section === 'left' ? leafIds[0] : leafIds[leafIds.length - 1]);
    if (!anchor) return undefined;
    const edgeId = section === 'left' ? leafIds[leafIds.length - 1] : leafIds[0];
    return { ...anchor, isEdge: isEmpty && (pinnedLayouts.get(edgeId)?.isEdge ?? false) };
  };

  // `isRowEnd` marks the first right-pinned header, pushed to the row's end when the columns don't fill the table.
  const renderHeader = (header: Header<TData, unknown>, isGroup: boolean, section: Section, isRowEnd = false) => {
    // Empty band cells: the select column's, TanStack placeholders (columns shallower than the deepest group) and
    // the hand-authored '#'-prefixed spacer groups.
    const isEmptyGroupHeader =
      isGroup && (header.column.id === 'select' || header.isPlaceholder || header.column.id.startsWith('#'));
    const pinned = getPinnedCellProps(
      isGroup ? getBandLayout(header, section, isEmptyGroupHeader) : pinnedLayouts.get(header.column.id),
      { isHeader: true },
    );
    // A pinned empty band is square and fills its row, so it covers the bands scrolling beneath it.
    const pinnedEmptyBandClass =
      isEmptyGroupHeader && pinned['data-pinned'] ? 'h-auto self-stretch overflow-visible rounded-none' : undefined;
    const rightEdgeClass = isRowEnd ? 'ml-auto' : undefined;

    if (header.column.id === 'select') {
      if (isGroup) {
        return (
          <TableHeadGroup
            key={header.id}
            data-pinned={pinned['data-pinned']}
            className={cn('w-[32px] justify-center bg-white', pinnedEmptyBandClass, pinned.className)}
            style={pinned.style}
          />
        );
      }
      return (
        <GridHeadCell
          key={header.id}
          data-pinned={pinned['data-pinned']}
          {...gridCellProps(header.column.id)}
          className={cn('w-[32px] justify-center bg-white', pinned.className, isGrid && 'focus-ring-inset')}
          style={pinned.style}
          data-testid={`table-header-${header.column.id}`}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
        </GridHeadCell>
      );
    }

    const canResize = header.column.getCanResize();
    const canReorder = !!table.options.onColumnOrderChange;
    const canPin = header.column.getCanPin() && !fixedPinning;
    const canHide = header.column.getCanHide();
    const canFilter = header.column.getCanFilter();
    const canSort = header.column.getCanSort();

    // Width styles for column resizing
    // For group headers spanning pinned sections, use section-aware size calculation
    // Note: Group headers that span sections can't use CSS variables reliably
    // because they need different sizes per section
    const groupSpan = isGroup ? getHeaderSizeForSection(header, section) : undefined;
    const headerSize = groupSpan ? groupSpan.size : header.getSize();
    let widthStyles: CSSProperties | undefined =
      canResize && !isGroup
        ? { width: `calc(var(--header-${toCssVarName(header.id)}-size) * 1px)` }
        : header.column.id !== 'select'
          ? { width: headerSize }
          : undefined;
    // Auto width drops the configured size from the table's min-content; the column shrinks to its unwrapped content.
    if (!isGroup && expandColumnId && header.column.id === expandColumnId) {
      widthStyles = {
        flexBasis: widthStyles?.width,
        width: 'auto',
        minWidth: 'max-content',
        flexGrow: 1,
        flexShrink: 1,
      };
    }

    // Render group header for non-empty groups
    if (isGroup) {
      if (isEmptyGroupHeader) {
        return (
          <TableHeadGroup
            key={header.id}
            data-pinned={pinned['data-pinned']}
            aria-colspan={groupSpan?.columnCount}
            className={cn('bg-white', pinnedEmptyBandClass, pinned.className, rightEdgeClass)}
            style={{ ...widthStyles, ...pinned.style }}
          />
        );
      }
      return (
        <TableHeadGroup
          key={header.id}
          data-pinned={pinned['data-pinned']}
          aria-colspan={groupSpan?.columnCount}
          className={cn(pinned.className, rightEdgeClass)}
          style={{ ...widthStyles, ...pinned.style }}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
        </TableHeadGroup>
      );
    }

    const sortDirection = header.column.getIsSorted();

    const headerContent = flexRender(header.column.columnDef.header, header.getContext());
    const headerText = typeof headerContent === 'string' ? headerContent : undefined;

    // Map tanstack sort state to ColumnMenu format
    const columnMenuSortDirection = sortDirection === 'asc' ? 'asc' : sortDirection === 'desc' ? 'desc' : 'none';

    // Map tanstack pin state to ColumnMenu format
    const isPinned = header.column.getIsPinned();
    const columnMenuPinPosition: PinPosition | undefined =
      isPinned === 'left' ? 'left' : isPinned === 'right' ? 'right' : 'none';

    // Map tanstack visibility state to ColumnMenu format
    const isVisible = header.column.getIsVisible();

    // Extract filter configuration from column meta
    const columnMeta = header.column.columnDef.meta as any;
    const filterValueConfig: FilterValueConfig | undefined = columnMeta?.filterValueConfig;
    const filterOperatorTypeConfig: FilterOperatorType[] | undefined = columnMeta?.filterOperatorTypeConfig;

    // Opt-in info/question tooltip rendered next to the header label
    const headerTooltip = columnMeta?.headerTooltip;
    const headerTooltipIcon = columnMeta?.headerTooltipIcon;

    // Get current filters from unified filter state (root-level items)
    const currentFilters: FilterLineItem[] = stateControl
      ? getColumnFilter(stateControl.filters, header.column.id)
      : [];

    // Disable dragging for select column
    const isSelect = header.column.id === 'select';
    const showDropIndicator = !!(activeId && overId === header.column.id);

    const movableColumnIds = movableColumnIdsIn(section);
    const moveIndex = movableColumnIds.indexOf(header.column.id);
    const canMove = canReorder && !isSelect && moveIndex !== -1 && movableColumnIds.length > 1;
    const columnSize = header.column.getSize();
    const { size = columnSize, minSize = 0, maxSize = Number.MAX_SAFE_INTEGER } = header.column.columnDef;
    const defaultSize = Math.min(Math.max(size, minSize), maxSize);

    // Prepare filter handlers
    const handleAddFilter =
      canFilter && stateControl
        ? (filter: ColumnFilter) => {
            // Add new filter to unified filter state
            if (filter.operatorType && filter.value !== undefined) {
              const newFilters = setColumnFilter(
                stateControl.filters,
                header.column.id,
                filter.operatorType,
                filter.value,
              );
              stateControl.setFilters(newFilters);
            }
          }
        : undefined;

    const handleRemoveFilter =
      canFilter && stateControl
        ? (filterId: string) => {
            // Remove specific filter from unified filter state
            const newFilters = removeColumnFilter(stateControl.filters, filterId);
            stateControl.setFilters(newFilters);
          }
        : undefined;

    const renderGridHead = () => {
      return (
        <GridHead
          data-testid={`table-header-${header.column.id}`}
          label={header.isPlaceholder ? undefined : headerText}
          sortLabel={headerText ?? header.column.id}
          tooltip={header.isPlaceholder ? undefined : headerTooltip}
          tooltipIcon={headerTooltipIcon}
          filter={canFilter && currentFilters.length > 0}
          isLoading={isLoading}
          canResize={canResize}
          canReorder={canReorder}
          // ColumnMenu integration
          canHide={canHide}
          canPin={canPin}
          canSort={canSort}
          canFilter={canFilter}
          showColumn={isVisible}
          fixedPinning={fixedPinning}
          onShowColumnChange={(checked) => {
            header.column.toggleVisibility(checked);
            // If hiding a pinned column, unpin it
            if (!checked && header.column.getIsPinned()) {
              header.column.pin(false);
            }
          }}
          sortDirection={canSort ? (columnMenuSortDirection as SortDirection) : undefined}
          onSortDirectionChange={
            canSort
              ? (direction) => {
                  if (direction === 'none') {
                    header.column.clearSorting();
                  } else {
                    header.column.toggleSorting(direction === 'desc');
                  }
                }
              : undefined
          }
          canMoveLeft={moveIndex > 0}
          canMoveRight={moveIndex !== -1 && moveIndex < movableColumnIds.length - 1}
          onMove={canMove && onMoveColumn ? (direction) => onMoveColumn(header.column.id, direction) : undefined}
          canNarrow={columnSize > minSize}
          canWiden={columnSize < maxSize}
          canResetWidth={columnSize !== defaultSize}
          onResize={canResize && onResizeColumn ? (action) => onResizeColumn(header.column.id, action) : undefined}
          pinPosition={columnMenuPinPosition}
          onPinPositionChange={(position) => {
            // Update pinning state
            if (position === 'none') {
              header.column.pin(false);
            } else if (position === 'left' || position === 'right') {
              header.column.pin(position);
            }
          }}
          data-pinned={pinned['data-pinned']}
          {...gridCellProps(header.column.id)}
          className={cn(
            'bg-white',
            canReorder || canSort ? 'pl-0' : undefined,
            pinned.className,
            rightEdgeClass,
            isGrid && 'focus-ring-inset',
          )}
          style={{ ...widthStyles, ...pinned.style }}
          // Pass resize handlers when column resizing is enabled
          header={canResize ? header : undefined}
          showDropIndicator={showDropIndicator}
        >
          {!header.isPlaceholder && typeof headerContent !== 'string' && headerContent}
        </GridHead>
      );
    };

    return (
      <ColumnFilterProvider
        key={header.id}
        filterId={header.column.columnDef.id}
        filterLabel={header.column.columnDef.header as string}
        filterValueConfig={filterValueConfig}
        filterOperatorTypeConfig={filterOperatorTypeConfig}
        currentFilters={currentFilters}
        onAddFilter={handleAddFilter}
        onRemoveFilter={handleRemoveFilter}
      >
        {canReorder ? (
          <Draggable
            key={header.id}
            id={header.column.id}
            provideDragHandleProps={!isSelect && canReorder}
            disabled={isSelect}
            asChild={true}
            isDragOverlay={true}
          >
            {renderGridHead()}
          </Draggable>
        ) : (
          renderGridHead()
        )}
      </ColumnFilterProvider>
    );
  };

  // Sortable items per section (excluding select, active, and hidden columns)
  const sortableItems = useMemo(() => {
    const visibleIn = (section: Section) =>
      columnGroups[section].filter((id) => {
        if (id === 'select' || id === activeId) return false;
        const column = table.getColumn(id);
        return column ? column.getIsVisible() : false;
      });
    return { left: visibleIn('left'), center: visibleIn('center'), right: visibleIn('right') };
  }, [table, columnGroups, activeId]);

  // One sortable list keeps a header mounted when pinning moves it to another section, so its open menu and focus
  // survive; the strategy still only shifts headers within the dragged header's own section.
  const allSortableItems = useMemo(
    () => [...sortableItems.left, ...sortableItems.center, ...sortableItems.right],
    [sortableItems],
  );
  const sectionSortingStrategy = useMemo<SortingStrategy>(() => {
    const sectionAt = (index: number) =>
      index < sortableItems.left.length
        ? 'left'
        : index < sortableItems.left.length + sortableItems.center.length
          ? 'center'
          : 'right';
    return (args) => {
      const { activeIndex, overIndex, index } = args;
      if (activeIndex < 0 || overIndex < 0) return null;
      const section = sectionAt(activeIndex);
      if (sectionAt(overIndex) !== section || sectionAt(index) !== section) return null;
      return horizontalListSortingStrategy(args);
    };
  }, [sortableItems]);

  // Read per render, not memoized on [table]: useReactTable keeps one stable table
  // object and mutates it in place, so a [table] memo would go stale when runtime
  // changes (e.g. hiding every leaf of a subgroup) alter the header depth.
  const headerGroups = table.getHeaderGroups();
  const headersDepth = headerGroups.length;

  const leftPinnedIds = columnGroups.left.filter((id) => pinnedLayouts.has(id));
  const leftEndStyle: CSSProperties = { position: 'sticky', left: `calc(${sumSizeVars(leftPinnedIds)} - 4px)` };
  const rightEndStyle: CSSProperties = { position: 'sticky', right: 0 };

  return (
    <GridHeader
      className={cn(
        'sticky top-0 z-10 bg-white',
        // Rounds the top-left cell's focus ring to the table's corner
        '[&>tr:first-child>*:first-child:focus-visible]:rounded-tl-lg',
      )}
    >
      {headerGroups.map((headerGroup, groupIndex) => {
        // Every row above the leaf row is a presentational group band; only the leaf
        // level gets fully-featured header cells (sort/menu/resize/drag).
        const isGroup = groupIndex < headersDepth - 1;
        const headersIn = (section: Section) =>
          !isGroup
            ? headerGroup.headers.filter((h) => columnGroups[section].includes(h.column.id))
            : headerGroup.headers.filter((baseHeader) =>
                baseHeader.getLeafHeaders().some((h) => columnGroups[section].includes(h.column.id)),
              );
        const selectHeader = headersIn('left').find((h) => h.column.id === 'select');

        return (
          <GridRow
            color="white"
            key={headerGroup.id}
            className={cn(isGroup && 'h-5.5 flex items-end')}
            data-grid-row={isGrid && !isGroup ? '' : undefined}
            aria-rowindex={indexRows ? groupIndex + 1 : undefined}
          >
            {/* Select column - always first, not sortable */}
            {selectHeader && renderHeader(selectHeader, isGroup, 'left')}

            <SortableContext id="headers" items={allSortableItems} strategy={sectionSortingStrategy}>
              {SECTIONS.flatMap((section) => {
                if (columnGroups[section].length === 0) return [];
                const cells: ReactNode[] = headersIn(section)
                  .filter((h) => h.column.id !== 'select')
                  .map((h, index) => {
                    const node = renderHeader(h, isGroup, section, section === 'right' && index === 0);
                    // A group split across sections renders once per section, so its key carries the section.
                    return isGroup ? <Fragment key={`${section}:${h.id}`}>{node}</Fragment> : node;
                  });
                if (!isGroup) {
                  cells.push(
                    <EndDropzone
                      key={`${section}-end-dropzone`}
                      section={section}
                      activeId={activeId}
                      overId={overId}
                      stickyStyle={section === 'left' ? leftEndStyle : section === 'right' ? rightEndStyle : undefined}
                    />,
                  );
                }
                return cells;
              })}
            </SortableContext>
          </GridRow>
        );
      })}
    </GridHeader>
  );
}

export { TableHeader };
